import { useState } from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { ArrowLeft, Check, Layers } from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import forwardThumb from "@/assets/book-covers/forward.webp"
import allRedThumb from "@/assets/book-covers/all_red.webp"
import editorialThumb from "@/assets/book-covers/editorial.webp"

const THUMBS = { forward: forwardThumb, all_red: allRedThumb, editorial: editorialThumb }

// The book cover: the coach picks one of Kingdom Factor's designs and writes
// the author lines. The book fills in the rest (the city, the leaders' names,
// the QR code). The preview is the print file itself, and shows a design
// as soon as the coach clicks it.
export default function BookCover({ currentUser, book, cover, designs = [] }) {
  const form = useForm({
    cover_design: cover.design,
    cover_author_name: cover.authorName || "",
    cover_author_credential: cover.credential || "",
    cover_author_bio: cover.bio || "",
  })
  const [previewKey, setPreviewKey] = useState(0)
  const locked = book.sentToPrint
  const changed =
    form.data.cover_design !== cover.design ||
    form.data.cover_author_name !== (cover.authorName || "") ||
    form.data.cover_author_credential !== (cover.credential || "") ||
    form.data.cover_author_bio !== (cover.bio || "")

  const save = () => {
    form.transform((data) => ({ book: data }))
    form.patch(`/app/coach/books/${book.id}`, { preserveScroll: true, onSuccess: () => setPreviewKey((key) => key + 1) })
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`Cover - ${book.title}`} />
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <Link href={`/app/coach/books/${book.id}`} className="rounded-md p-2 hover:bg-zinc-100" aria-label="Back to the book">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-kf-red">{book.title}</div>
            <h1 className="text-2xl font-bold text-zinc-900">Book cover</h1>
            <p className="text-zinc-600">Pick a Kingdom Factor cover design and check the words about you. Your city and your leaders' names go on the cover automatically.</p>
          </div>
          {cover.approved && <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> Approved</span>}
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Preview (back, spine, front)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <iframe key={previewKey} title="Cover preview" src={`/app/coach/books/${book.id}/cover_preview?design=${form.data.cover_design}&v=${previewKey}`}
              className="h-[420px] w-full rounded-md border bg-zinc-200" />
            <p className="flex items-center gap-1.5 text-xs text-zinc-500">
              <Layers className="h-3.5 w-3.5" /> The dashed lines show the spine. Its width comes from the page count (about {cover.estimatedPages} pages now); the final cover is sized at assembly.
            </p>
          </CardContent>
        </Card>

        {!locked && (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <Card>
              <CardHeader><CardTitle className="text-base">Cover design</CardTitle></CardHeader>
              <CardContent>
                <p className="mb-3 text-sm text-zinc-600">Every design uses the same Kingdom Factor words. Click a design to see it in the preview.</p>
                <div className="grid grid-cols-3 gap-3">
                  {designs.map((design) => (
                    <DesignTile key={design.key} selected={form.data.cover_design === design.key}
                      onClick={() => form.setData("cover_design", design.key)} label={design.name} src={THUMBS[design.key]} />
                  ))}
                </div>
                {form.errors.cover_design && <p className="mt-2 text-xs text-red-600">{form.errors.cover_design}</p>}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-4 pt-6">
                <div className="space-y-1.5">
                  <Label htmlFor="author">Author name on the cover</Label>
                  <Input id="author" value={form.data.cover_author_name} maxLength={80} placeholder={cover.authorDefault}
                    onChange={(e) => form.setData("cover_author_name", e.target.value)} />
                  {form.errors.cover_author_name && <p className="text-xs text-red-600">{form.errors.cover_author_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="credential">One line under your name</Label>
                  <Input id="credential" value={form.data.cover_author_credential} maxLength={80} placeholder="Kingdom Factor Coach"
                    onChange={(e) => form.setData("cover_author_credential", e.target.value)} />
                  <p className="text-xs text-zinc-500">For example: Kingdom Factor Coach · Founder, Reed Builders.</p>
                  {form.errors.cover_author_credential && <p className="text-xs text-red-600">{form.errors.cover_author_credential}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bio">Short bio for the back cover</Label>
                  <Textarea id="bio" rows={5} maxLength={400} value={form.data.cover_author_bio}
                    onChange={(e) => form.setData("cover_author_bio", e.target.value)} />
                  <p className="text-xs text-zinc-500">
                    Two or three sentences: your work with Kingdom Factor, your business background, and your community.
                    Your name prints in bold at the start. {form.data.cover_author_bio.length}/400
                  </p>
                  {form.errors.cover_author_bio && <p className="text-xs text-red-600">{form.errors.cover_author_bio}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={save} disabled={!changed || form.processing}>Save and preview</Button>
                  <Button variant="outline" disabled={changed || cover.approved} onClick={() => router.post(`/app/coach/books/${book.id}/approve_cover`)}>
                    <Check className="h-4 w-4" /> Approve cover
                  </Button>
                </div>
                {changed && <p className="text-xs text-zinc-500">Save to keep your changes. A changed cover must be approved again.</p>}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </CoachLayout>
  )
}

function DesignTile({ selected, onClick, label, src }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={selected}
      className={`overflow-hidden rounded-md border-2 text-left ${selected ? "border-kf-red ring-2 ring-kf-red/30" : "border-transparent hover:border-zinc-300"}`}>
      <img src={src} alt={`${label} cover design`} className="aspect-[1689/2625] w-full bg-zinc-100 object-cover" />
      <div className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-700">
        {selected && <Check className="h-3.5 w-3.5 text-kf-red" />}{label}
      </div>
    </button>
  )
}
